import React, { useState, useRef, useEffect } from 'react'
import { useForm } from 'react-hook-form/dist/react-hook-form.ie11'
import { withStyles } from '@material-ui/styles'
import {
  Button,
  Typography,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Tooltip,
  IconButton
} from '@material-ui/core'
import { InfoOutlined } from '@material-ui/icons'
import { red } from '@material-ui/core/colors'
import { EmailField, FormField, PasswordField, Link } from '../index'
import { t as i18n } from 'ttag'
import { isEmpty } from 'lodash'

const styles = theme => ({
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(1)
  },
  stagingLabel: {
    marginTop: 16
  },
  button: {
    marginTop: theme.spacing(2),
    float: 'right'
  },
  link: {
    float: 'left',
    marginTop: theme.spacing(2)
  },
  linkContainer: {
    width: '100%',
    float: 'left'
  },
  error: {
    color: red[900],
    marginTop: theme.spacing(2)
  }
})

const Form = props => {
  const {
    classes,
    isIdentifierFirstLogin,
    sessionKey,
    loginFormUrl,
    registerLink,
    usernameRecoverLink,
    passwordRecoverLink,
    loginFailed,
    authFailed,
    utid
  } = props

  const getFields = defaultValue => {
    let fields = {
      tenant: defaultValue,
      email: defaultValue,
      username: defaultValue,
      password: defaultValue,
      remember: false
    }

    return fields
  }

  const [values, setValues] = useState(getFields(''))
  const { register, handleSubmit, errors, getValues } = useForm()

  useEffect(() => {
    setValues({ ...values, tenant: utid, username: `${values.email}@${utid}` })
  }, [])

  const handleChange = name => event => {
    setValues({
      ...values,
      [name]: event.target.value,
      ...(name === 'tenant'
        ? { username: `${values.email}@${event.target.value}` }
        : {}),
      ...(name === 'email'
        ? { 
            username: `${event.target.value.toLowerCase()}@${values.tenant}`,
            email: event.target.value.toLowerCase()
          }
        : {}),
    })
  }

  const handleChangeCheckbox = name => event => {
    setValues({ ...values, [name]: event.target.checked })
  }

  const onSubmit = data => {
    setValues({
      ...values,
      username: `${values.email.trim()}@${values.tenant.trim()}`,
      tenant: values.tenant.trim(),
      email: values.email.trim()
    })
    if (isEmpty(errors)) {
      document.getElementById('Login-container').submit()
    }
  }

  return (
    <form
      id='Login-container'
      className={classes.form}
      onSubmit={handleSubmit(onSubmit)}
      noValidate={true}
      method='post'
      action={loginFormUrl}
    >
      {process.env.REACT_APP_ENVIRONMENT === 'staging' && (
        <Typography
          align={'center'}
          className={classes.stagingLabel}
          data-qid={'Text-stagingEnvironment'}
          gutterBottom
        >
          Staging Environment
        </Typography>
      )}

      <FormField
        id={`TenantField-Login-tenant`}
        data-cid='TenantField-root'
        label={i18n`Domain name`}
        name='tenant'
        onChange={handleChange('tenant')}
        value={values.tenant}
        errors={errors}
        inputRef={register({ required: i18n`Enter your domain name` })}
        required
        autoFocus={true}
        InputProps={{
          endAdornment: (
            <InputAdornment position='end'>
              <IconButton
                data-cid='Tenant-button-toggle'
                aria-label={i18n`Tenant information`}
              >
                <Tooltip
                  placement='right'
                  title='Your domain name is your company domain name. This is usually the portion of your email address that comes after the @ symbol. For example: the domain name for john.smith@lseg.com is lseg.com"'
                >
                  <InfoOutlined />
                </Tooltip>
              </IconButton>
            </InputAdornment>
          )
        }}
      />

      <FormField
        id={`EmailField-Login-tenant`}
        data-cid='EmailField-root'
        label={i18n`Email`}
        name='email'
        onChange={handleChange('email')}
        value={values.email.toLowerCase()}
        errors={errors}
        inputRef={register({ required: i18n`Enter your email` })}
        required
      />

      <EmailField
        id={'Login-username'}
        onChange={handleChange('username')}
        value={values.username}
        errors={errors}
        inputRef={register({ required: i18n`Enter your username` })}
      />

      <PasswordField
        id={'Login-password'}
        onChange={handleChange('password')}
        value={values.password}
        errors={errors}
        inputRef={register({ required: i18n`Enter your password` })}
      />

      <input type='hidden' name='sessionDataKey' value={sessionKey} />

      <FormControlLabel
        data-qid={'Login-checkbox-remember'}
        control={
          <Checkbox
            id='chkRemember'
            name='chkRemember'
            checked={values.remember}
            onChange={handleChangeCheckbox('remember')}
            value={values.remember ? 'on' : 'off'}
          />
        }
        label={<Typography variant='caption'>{i18n`Remember me`}</Typography>}
      />

      <div className={classes.buttonsContainer}>
        <Button
          data-qid={'Login-button'}
          type='submit'
          variant='contained'
          color='primary'
          fullWidth
          className={classes.button}
        >
          {i18n`Log in`}
        </Button>

        <div className={classes.link}>
          <Link data-qid={'Login-link-forgot'} to={passwordRecoverLink}>
            <Typography variant='caption'>{i18n`Forgot your password?`}</Typography>
          </Link>
        </div>
      </div>
    </form>
  )
}

export default withStyles(styles)(Form)
